/**
 * Someone else's newer section, fact by fact, for `ConflictNotice`: every
 * section editor (the registrar record and the seat's edit dialog) lists the
 * facts that differ from what was typed, with the labels the form uses and the
 * values as a reader says them.
 */
import type { ConflictFact } from "@/components/ui/conflict-notice";
import type { Program, Semester } from "@/lib/api/academicStructure";
import { MEETING_PROVIDER_LABEL, MODALITY_LABEL } from "@/lib/api/courses";
import {
  NONE,
  TIME_OPTIONS,
  WEEKDAY_OPTIONS,
  type CourseForm,
  type InstructorOption,
} from "@/lib/course-form";
import { courseTimezoneOptionLabel } from "@/lib/course-schedule";

export const COURSE_FACT_LABEL: Record<keyof CourseForm, string> = {
  code: "Code",
  title: "Title",
  credits: "Credits",
  modality: "Modality",
  programId: "Program",
  semesterId: "Term",
  instructorFacultyProfileId: "Primary instructor",
  meetingProvider: "Meeting provider",
  meetingWeekdays: "Meeting days",
  meetingStartsAt: "Starts",
  meetingEndsAt: "Ends",
  timezone: "Time zone",
  meetingUrl: "Meeting link",
  location: "Location",
  capacity: "Capacity",
  imageUrl: "Image link",
};

const FACT_ORDER = Object.keys(COURSE_FACT_LABEL) as Array<keyof CourseForm>;

export interface CourseFactNames {
  programs: Program[];
  semesters: Semester[];
  instructors: InstructorOption[];
}

/** One fact of a section form as a reader says it; blank is no value. */
export function readCourseFact(form: CourseForm, key: keyof CourseForm, names: CourseFactNames): string {
  const time = (value: string) => {
    const hhmm = value.slice(0, 5);
    return TIME_OPTIONS.find((option) => option.value === hhmm)?.label ?? value;
  };
  const chosen = (value: string) => (value === NONE ? "" : value);
  switch (key) {
    case "modality":
      return MODALITY_LABEL[form.modality];
    case "meetingProvider":
      return MEETING_PROVIDER_LABEL[form.meetingProvider];
    case "meetingWeekdays":
      return [...form.meetingWeekdays]
        .sort((a, b) => a - b)
        .map((day) => WEEKDAY_OPTIONS.find(([value]) => value === day)?.[1] ?? String(day))
        .join(", ");
    case "meetingStartsAt":
      return form.meetingStartsAt ? time(form.meetingStartsAt) : "";
    case "meetingEndsAt":
      return form.meetingEndsAt ? time(form.meetingEndsAt) : "";
    case "timezone":
      return form.timezone ? courseTimezoneOptionLabel(form.timezone) : "";
    case "programId": {
      const id = chosen(form.programId);
      return id ? names.programs.find((program) => program.id === id)?.name ?? id : "";
    }
    case "semesterId": {
      const id = chosen(form.semesterId);
      return id ? names.semesters.find((semester) => semester.id === id)?.name ?? id : "";
    }
    case "instructorFacultyProfileId": {
      const id = chosen(form.instructorFacultyProfileId);
      return id ? names.instructors.find((option) => option.faculty_profile_id === id)?.label ?? id : "";
    }
    default:
      return form[key].trim();
  }
}

/** Their facts that differ from mine, in form order, limited to `keys` when given. */
export function courseConflictFacts(
  mine: CourseForm,
  theirs: CourseForm,
  names: CourseFactNames,
  keys: ReadonlyArray<keyof CourseForm> = FACT_ORDER,
): ConflictFact[] {
  return FACT_ORDER
    .filter((key) => keys.includes(key))
    .filter((key) => readCourseFact(mine, key, names) !== readCourseFact(theirs, key, names))
    .map((key) => ({ label: COURSE_FACT_LABEL[key], theirs: readCourseFact(theirs, key, names) }));
}
